import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl, useIntl } from 'react-intl';
import Axios from 'axios';
import intlShape from '@/shared/utils/intlShape';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import enumCache from '@/shared/services/EnumCacheService';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import api from '@/shared/api';
import config from '@/config';
import MultiSelect, { addItem, removeItem, splitItems } from './MultiSelect';

const messages = defineMessages({
  InvoiceSchedules: 'Invoice schedules',
  NoInvoicingSchedule: 'No invoicing schedule',
  WithInvoicingSchedule: 'With invoicing schedule',
  InvoicingViaRelations: 'Invoicing via relations',
});

const invoiceTypeKeys = {
  0: '33333333-3333-3333-3333-333333333333',
  1: '44444444-4444-4444-4444-444444444444',
  2: '55555555-5555-5555-5555-555555555555',
};

class InvoiceScheduleTypeMultiSelect extends React.Component {
  state = {
    items: [],
  };

  componentDidMount() {
    const { intl, hideInvoicingVia } = this.props;
    this.cancelToken = Axios.CancelToken.source();

    const invoiceSchedulesPromise = api.base.get(`${config.appUrls.api}/api/invoiceschedule/get`, {
      cancelToken: this.cancelToken.token,
    });

    Promise.all([enumCache.getTranslated('invoicingType', intl), invoiceSchedulesPromise]).then(
      ([invoicingTypes, { data: invoiceSchedules }]) => {
        const fixedItems = [
          {
            id: '00000000-0000-0000-0000-000000000000',
            label: intl.formatMessage(messages.NoInvoicingSchedule),
          },
          {
            id: '11111111-1111-1111-1111-111111111111',
            label: intl.formatMessage(messages.WithInvoicingSchedule),
          },
        ];
        if (!hideInvoicingVia) {
          fixedItems.push({
            id: '22222222-2222-2222-2222-222222222222',
            label: intl.formatMessage(messages.InvoicingViaRelations),
          });
        }
        const items = [
          ...fixedItems,
          ...invoicingTypes.map(x => ({
            ...x,
            id: invoiceTypeKeys[x.id],
          })),
          ...invoiceSchedules
            .map(x => ({
              id: x.id,
              label: intl.formatMessage(listItemMsg('invoiceschedule', x.id, x.name)),
            }))
            .sort(dynamicSort('label')),
        ];
        this.setState({ items });
      },
    ).catch(error => {
      // Only handle non-cancellation errors
      if (!Axios.isCancel(error)) {
        console.error('Error loading invoice schedule types:', error);
      }
    });
  }
  componentWillUnmount() {
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to leaving page.');
    }
  }
  render() {
    const { selection, onChange, intl, placeholder } = this.props;
    const { items } = this.state;
    const [selectedItems, selectableItems] = splitItems(items, selection);

    return (
      <MultiSelect
        selectedItem={selectedItems}
        onItemRemoved={item => onChange(removeItem(selection, item))}
        onItemSelected={item => onChange(addItem(selection, item))}
        items={selectableItems}
        placeholder={placeholder || intl.formatMessage(messages.InvoiceSchedules)}
      />
    );
  }
}

InvoiceScheduleTypeMultiSelect.propTypes = {
  selection: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired, // (newSelection)=>{}
  placeholder: PropTypes.string,
  intl: intlShape.isRequired,
  hideInvoicingVia: PropTypes.bool,
};

InvoiceScheduleTypeMultiSelect.defaultProps = {
  placeholder: null,
  hideInvoicingVia: false,
};

export default injectIntl(InvoiceScheduleTypeMultiSelect);
